import React, { useContext, useState } from 'react';
import { Formik } from 'formik';

import About from './Pages/About';
import CostToPlant from './Pages/CostToPlant';
import CropMixChart from './Pages/CropMixChart';
import CropSelection from './Pages/CropSelection';
import CustomPlantingArea from './Pages/CustomPlantingArea';
import LocationSelection from './Pages/LocationSelection';
import MarketPrice from './Pages/MarketPrice';
import PageContainer from './PageContainer';
import PlantingDistribution from './Pages/PlantingDistribution';
import Scenarios from './Pages/Scenarios';
import TypicalCropYield from './Pages/TypicalCropYield';
import WaterAccess from './Pages/WaterAccess';

import { PageContext, pages } from '../../page-context';
import { LanguageContext } from '../../language-context';

import calculateScenarios from '../../utils/calculateScenarios';
import getSchema from './schema';

const initialValues = {
  crops: [],
  landAvailable: '',
  landAvailableUnit: 'ha',
  soilCode: 112,
  waterAccessFlowRate: '',
  waterAccessHours: '',
  waterAccessMonths: '',
  weatherStation: 'chivay',
};

function renderPage(
  actions,
  appStyles,
  language,
  pageNumber,
  results,
  toggleDisableNextStep
) {
  switch (pageNumber) {
    case 1:
      return (
        <LocationSelection
          actions={actions}
          appStyles={appStyles}
          pageContent={pages[pageNumber + 1][language].fields}
          toggleDisableNextStep={toggleDisableNextStep}
        />
      );
    case 2:
      return (
        <WaterAccess
          actions={actions}
          appStyles={appStyles}
          pageContent={pages[pageNumber + 1][language].fields}
          toggleDisableNextStep={toggleDisableNextStep}
        />
      );
    case 3:
      return (
        <CropSelection
          actions={actions}
          language={language}
          pageContent={pages[pageNumber + 1][language].fields}
          toggleDisableNextStep={toggleDisableNextStep}
          unit={actions.values.landAvailableUnit}
        />
      );
    case 4:
      return (
        <MarketPrice
          actions={actions}
          appStyles={appStyles}
          pageContent={pages[pageNumber + 1][language].fields}
          unit={pages[pageNumber + 1][language].unit}
          toggleDisableNextStep={toggleDisableNextStep}
        />
      );
    case 5:
      return (
        <CostToPlant
          actions={actions}
          appStyles={appStyles}
          pageContent={pages[pageNumber + 1][language].fields}
          unit={
            pages[pageNumber + 1][language].unit[
              actions.values.landAvailableUnit
            ]
          }
          toggleDisableNextStep={toggleDisableNextStep}
        />
      );
    case 6:
      return (
        <TypicalCropYield
          actions={actions}
          appStyles={appStyles}
          pageContent={pages[pageNumber + 1][language].fields}
          unit={
            pages[pageNumber + 1][language].unit[
              actions.values.landAvailableUnit
            ]
          }
          toggleDisableNextStep={toggleDisableNextStep}
        />
      );
    case 7:
      return (
        <Scenarios
          pageContent={pages[pageNumber + 1][language].fields}
          toggleDisableNextStep={toggleDisableNextStep}
          values={actions.values}
        />
      );
    case 8:
      return (
        <PlantingDistribution
          actions={actions}
          appStyles={appStyles}
          pageContent={pages[pageNumber + 1][language].fields}
          unit={pages[pageNumber + 1][language].unit}
          toggleDisableNextStep={toggleDisableNextStep}
        />
      );
    case 9:
      return (
        <CustomPlantingArea
          actions={actions}
          appStyles={appStyles}
          pageContent={pages[pageNumber + 1][language].fields}
          unit={pages[pageNumber + 1][language].unit}
          toggleDisableNextStep={toggleDisableNextStep}
        />
      );
    case 10:
      return (
        <CropMixChart
          appStyles={appStyles}
          chartName="cropmix"
          results={results ? (results.cropMix ? results.cropMix : []) : []}
          language={language}
          pageContent={pages[pageNumber + 1][language].fields}
          toggleDisableNextStep={toggleDisableNextStep}
          unit={
            pages[pageNumber + 1][language].unit[
              actions.values.landAvailableUnit
            ]
          }
        />
      );
    case 11:
      return (
        <CropMixChart
          appStyles={appStyles}
          chartName="profit"
          language={language}
          results={results ? (results.profits ? results.profits : []) : []}
          pageContent={pages[pageNumber + 1][language].fields}
          toggleDisableNextStep={toggleDisableNextStep}
        />
      );
    case 12:
      return (
        <CropMixChart
          appStyles={appStyles}
          chartName="waterneeds"
          language={language}
          results={results ? (results.waterUse ? results.waterUse : []) : []}
          pageContent={pages[pageNumber + 1][language].fields}
          toggleDisableNextStep={toggleDisableNextStep}
          unit={
            pages[pageNumber + 1][language].unit[
              actions.values.landAvailableUnit
            ]
          }
        />
      );
    case 13:
      return (
        <CropMixChart
          appStyles={appStyles}
          chartName="irrigationtime"
          language={language}
          results={
            results
              ? results.irrigationRequiredTime
                ? results.irrigationRequiredTime
                : []
              : []
          }
          pageContent={pages[pageNumber + 1][language].fields}
          toggleDisableNextStep={toggleDisableNextStep}
        />
      );
    case 14:
      return (
        <About
          lang={language}
          pageContent={pages[pageNumber + 1][language].fields}
        />
      );
    default:
      return (
        <LocationSelection
          actions={actions}
          pageContent={pages[pageNumber + 1][language].fields}
          toggleDisableNextStep={toggleDisableNextStep}
        />
      );
  }
}

export default function Form({
  appStyles,
  setFormikActions,
  toggleDisableNextStep,
}) {
  const language = useContext(LanguageContext);
  const pageNumber = useContext(PageContext);

  const [results, setResults] = useState(null);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, actions) => {
        const results = calculateScenarios(values, language);
        setResults(results);
        actions.setSubmitting(false);
      }}
      validationSchema={getSchema(language)}
    >
      {(actions) => (
        <PageContainer
          actions={actions}
          setFormikActions={setFormikActions}
          title={
            typeof pages[pageNumber + 1][language].title === 'string'
              ? pages[pageNumber + 1][language].title
              : pages[pageNumber + 1][language].title[
                  actions.values.landAvailableUnit
                ]
          }
        >
          {renderPage(
            actions,
            appStyles,
            language,
            pageNumber,
            results,
            toggleDisableNextStep
          )}
        </PageContainer>
      )}
    </Formik>
  );
}
